import dedentContainers from "@ext/markdown/core/Parser/MdParser/dedentContainers";
import { encodeCommentText } from "@ext/markdown/elements/sourceComment/logic/commentText";
import type { Schema } from "../../render/logic/Markdoc/index";

export interface MdParserOptions {
	tags: { [name: string]: Schema };
}

enum PreParseSyntax {
	Pre = 1 << 0,
	Comment = 1 << 1,
	Table = 1 << 2,
	Include = 1 << 3,
	Id = 1 << 4,
	Dash = 1 << 5,
	Square = 1 << 6,
	Property = 1 << 7,
	Formula = 1 << 8,
	Br = 1 << 9,
	Kbd = 1 << 10,
	Html = 1 << 11,
}

const allPreParseSyntax = (1 << 12) - 1;

const lineCount = (text: string) => text.split("\n").length;

/** An ignored span (code, link, comment) matches without the group; `str.replace(undefined, …)` would replace the
 *  word "undefined" in it. */
const replaceGroup = (replacement: string) => (str: string, group?: string) =>
	group ? str.replace(group, replacement) : str;

/** `${…}` is a value substitution — a secret placeholder, a shell variable in a command example — not a
 *  formula delimiter: `$…$` can neither open nor close on one. */
const substitutionBody = String.raw`\{[^}\r\n]+\}`;
const substitution = String.raw`\$${substitutionBody}`;

const leadingWhitespace = (line: string) => /^[ \t]*/.exec(line)[0];

/** Where the text of a line starts: past its indentation, and past the marker of a list item it opens. */
const contentIndent = (line: string) => {
	const listItem = /^[ \t]*(?:[-*+]|\d+[.)])[ \t]{1,4}/.exec(line);
	return listItem ? " ".repeat(listItem[0].length) : leadingWhitespace(line);
};

/**
 * The indentation both lines around a run of blank lines have — that of the block the run is in. A line
 * written at column 0 would end a list item; one indented past a paragraph would start a code block.
 */
const sharedIndent = (text: string, runStart: number, runEnd: number) => {
	const before = contentIndent(text.slice(text.lastIndexOf("\n", runStart - 1) + 1, runStart));
	const after = leadingWhitespace(text.slice(runEnd));
	return before.length < after.length ? before : after;
};

export default class MdParser {
	private _tags: { [name: string]: Schema };
	private _escapeDoubleQuotesRegExp: RegExp;
	private _sourceCommentRegExp: RegExp;
	private _includeRegExp: RegExp;
	private _formulaRegExp: RegExp;
	private _squareRegExp: RegExp;
	private _arrowRegExp: RegExp;
	private _oldArrowRegExp: RegExp;
	private _dashRegExp: RegExp;
	private _idRegExp: RegExp;
	private _brRegExp: RegExp;
	private _kbdRegExp: RegExp;
	private _emptyParagraphRegExp: RegExp;
	private _findHtmlRegExp: RegExp;
	private _findHtmlTagRegExp: RegExp;
	private _preParseSyntaxRegExp =
		/<!--|<pre>|<kbd>|<html|<br|{% table|{%property|\[\/\/\]:|\[comment\]:|\[include:|\[html|[[{] ?#|-|\[|\{|\$/g;

	private _backDashRegExp: RegExp;
	private _backArrowRegExp: RegExp;

	private _listWithAnEmptyItem: RegExp;
	private _table: RegExp;
	private _emptyTableCell: RegExp;

	// On the way back the written comments are in the text again, and `-->` in them is not an arrow.
	private _findCommentToIgnore = String.raw`<!--[\s\S]*?-->`;
	private _findLinkToIgnore = String.raw`(?:\[(?:[^\\\]]|\\.)*?\]\(\s*(?:[^)\s\\<>"']|\\.)+?\s*\))|<[^>]*:[^>]*>`;
	// A code span opens and closes with backtick runs of one length, as in CommonMark.
	private _findInlineCodeToIgnore =
		"(?<!`)(?:`(?!`).*?(?<!`)`|``(?!`).*?(?<!`)``|```(?!`).*?(?<!`)```|````+(?!`).*?(?<!`)````+)(?!`)";
	// A fence starts its line, after indentation or a list or quote marker at most. Read from the middle of a line,
	// three backticks of inline code hid the text from every rewrite down to the next three in the article.
	private _fenceStart = String.raw`^[ \t>*+\-\d.)]*`;
	private _fenceEnd = String.raw`^[ \t>]*`;
	// A fence longer than three backticks holds content that itself contains three: it has to be
	// skipped by its own length, or the tail of the block stops counting as code.
	private _findLongBlockCodeToIgnore =
		`${this._fenceStart}\`{4,}[^\\n]*\\n[\\s\\S]*?${this._fenceEnd}\`{4,}[ \\t\\r]*$`;
	private _findBlockCodeToIgnore =
		`${this._findLongBlockCodeToIgnore}|\`\`\`[^(\`\`\`)]*?\`\`\`[^(\`\`\`)]*?\`\`\`\n\r?\`\`\`|${this._fenceStart}\`{3,}[^\\n]*\\n[\\s\\S]*?${this._fenceEnd}\`{3,}[ \\t\\r]*$|\\\\.`;
	private _findHtmlToIgnore = String.raw`\[html.*][\s\S]*?\[\/html\]|<html.*>[\s\S]*?<\/html\>`;

	private _preTagRegExp: RegExp;

	constructor(preParserOptions: MdParserOptions = null) {
		const flags = "gm";
		this._tags = preParserOptions?.tags ?? {};
		this._escapeDoubleQuotesRegExp = this._createIgnoreRegExp(String.raw`.*?[^\\](").*?`);
		this._emptyParagraphRegExp = this._createIgnoreRegExp(String.raw`(\r?\n{4,})`);
		// Comments are taken out before anything else reads the text, so this one skips on its own what
		// is not Markdown text. Not links: their autolink branch matched `<!-- TODO: fix -->` whole.
		this._sourceCommentRegExp = new RegExp(
			[
				String.raw`(<!--[\s\S]*?-->)`,
				// The comment forms of Markdown: a link reference nobody uses, pointing at `#` or nowhere.
				String.raw`\[(?:\/\/|comment)\]:[ \t]*(?:#|<>)[ \t]+(?:\((.*)\)|"(.*)"|'(.*)')[ \t]*$`,
				this._findInlineCodeToIgnore,
				this._findBlockCodeToIgnore,
				this._findHtmlToIgnore,
				String.raw`<pre>[\s\S]*?<\/pre>`,
			].join("|"),
			flags,
		);
		this._includeRegExp = this._createIgnoreRegExp(String.raw`^ *(#*) *\[include:([^\n\]]*)\]`);
		this._formulaRegExp = this._createIgnoreRegExp(
			String.raw`{\s?.*?\s?}|(\$[^\$\r\n][^\$\r\n]*?\$(?!${substitutionBody}))|(\$\$[^\$\r\n].*?\$\$)`,
			substitution,
		);
		this._squareRegExp = this._createIgnoreRegExp(String.raw`\[(.*?)\](\()?`);
		this._arrowRegExp = this._createIgnoreRegExp(String.raw`\\->|[^\\\r\n]?(->)`);
		this._oldArrowRegExp = this._createIgnoreRegExp(String.raw`\\-->|[^\\\r\n]?(-->)`);
		this._dashRegExp = this._createIgnoreRegExp(String.raw`.?-->.?|\\--|.?--[-]+|[^\\\n\r]?(--)`);
		this._idRegExp = this._createIgnoreRegExp(String.raw`[[{] ?(#.*?) ?[\]}]`);
		this._brRegExp = this._createIgnoreRegExp(String.raw`(<br>|<br\/>)`);
		this._kbdRegExp = this._createIgnoreRegExp(String.raw`<kbd>([\s\S]*?)<\/kbd>`);
		this._backDashRegExp = this._createIgnoreRegExp("(—)");
		this._backArrowRegExp = this._createIgnoreRegExp("(→)");
		// An HTML block starts its line, after indentation or a list or quote marker at most. Read from the middle
		// of a line of text, it would swallow the article down to the next closing tag.
		this._findHtmlRegExp = this._createBlockCodeIgnoreRegExp(
			String.raw`(^[ \t>*+\-\d.)]*)\[html.*]([\s\S]*?)\[\/html\]`,
		);
		this._findHtmlTagRegExp = this._createBlockCodeIgnoreRegExp(
			String.raw`(^[ \t>*+\-\d.)]*)(<html[^>]*>)([\s\S]*?)<\/html>`,
		);
		this._listWithAnEmptyItem = new RegExp(String.raw`^[ \t]*(?:\d+\.|-)[ \t]*$`, flags);
		this._table = new RegExp(String.raw`{% table\s*([^%]*)%}([\s\S]*?){% \/table %}`, flags);
		this._emptyTableCell = new RegExp(String.raw`^(?:\*)[ \t]*$`, flags);
		this._preTagRegExp = new RegExp(String.raw`<pre>([\s\S]*?)<\/pre>`, flags);
	}

	use(tag: Schema) {
		this._tags[tag.render.toLowerCase()] = tag;
	}

	/**
	 * Whether every rewrite of the last `preParse` left the document as long as it found it. Asked per
	 * step: two rewrites can cancel each other out and leave a document that looks faithful end to end
	 * while every line between them has moved.
	 */
	get linesPreserved(): boolean {
		return this._linesPreserved;
	}

	private _linesPreserved = true;

	private _rewrite(before: string, after: string): string {
		if (lineCount(after) !== lineCount(before)) this._linesPreserved = false;
		return after;
	}

	preParse(content: string): string {
		let newContent = content;
		this._linesPreserved = true;
		const syntax = this._detectPreParseSyntax(content);
		if (syntax & PreParseSyntax.Comment)
			newContent = this._rewrite(newContent, this._sourceCommentParser(newContent));
		newContent = this._rewrite(newContent, dedentContainers(newContent));
		let shouldParseFormula = Boolean(syntax & PreParseSyntax.Formula);
		if (syntax & PreParseSyntax.Pre) newContent = this._rewrite(newContent, this._preTagParser(newContent));
		if (syntax & PreParseSyntax.Table) newContent = this._rewrite(newContent, this._tableParser(newContent));
		if (syntax & PreParseSyntax.Include) newContent = this._rewrite(newContent, this._includeParse(newContent));
		if (syntax & PreParseSyntax.Id) newContent = this._rewrite(newContent, this._idParser(newContent));
		if (syntax & PreParseSyntax.Dash) newContent = this._rewrite(newContent, this._dashArrowParser(newContent));
		if (syntax & PreParseSyntax.Square) {
			const contentBeforeSquareParser = newContent;
			newContent = this._rewrite(newContent, this._squareBracketsParser(newContent));
			if (newContent !== contentBeforeSquareParser) shouldParseFormula = true;
		}
		if (syntax & PreParseSyntax.Property) newContent = this._rewrite(newContent, this._propertyParser(newContent));
		if (shouldParseFormula) newContent = this._rewrite(newContent, this._formulaParser(newContent));
		if (syntax & PreParseSyntax.Br) newContent = this._rewrite(newContent, this._brParser(newContent));
		if (syntax & PreParseSyntax.Kbd) newContent = this._rewrite(newContent, this._kbdParser(newContent));
		newContent = this._rewrite(newContent, this._emptyParagraphParser(newContent));
		if (syntax & PreParseSyntax.Html) newContent = this._rewrite(newContent, this._htmlParser(newContent));
		return newContent;
	}

	private _detectPreParseSyntax(content: string): number {
		let syntax = 0;
		this._preParseSyntaxRegExp.lastIndex = 0;
		let match = this._preParseSyntaxRegExp.exec(content);
		while (syntax !== allPreParseSyntax && match) {
			switch (match[0]) {
				case "<pre>":
					syntax |= PreParseSyntax.Pre;
					break;
				case "[//]:":
				case "[comment]:":
				case "<!--":
					syntax |= PreParseSyntax.Comment;
					break;
				case "{% table":
					syntax |= PreParseSyntax.Table | PreParseSyntax.Formula;
					break;
				case "[include:":
					syntax |= PreParseSyntax.Include | PreParseSyntax.Square | PreParseSyntax.Formula;
					break;
				case "-":
					syntax |= PreParseSyntax.Dash;
					break;
				case "{%property":
					syntax |= PreParseSyntax.Property | PreParseSyntax.Formula;
					break;
				case "$":
				case "{":
					syntax |= PreParseSyntax.Formula;
					break;
				case "<br":
					syntax |= PreParseSyntax.Br;
					break;
				case "<kbd>":
					syntax |= PreParseSyntax.Kbd;
					break;
				case "[html":
					syntax |= PreParseSyntax.Html | PreParseSyntax.Square;
					break;
				case "<html":
					syntax |= PreParseSyntax.Html;
					break;
				case "[#":
				case "[ #":
					syntax |= PreParseSyntax.Id | PreParseSyntax.Square | PreParseSyntax.Formula;
					break;
				case "{#":
				case "{ #":
					syntax |= PreParseSyntax.Id | PreParseSyntax.Formula;
					break;
				case "[":
					syntax |= PreParseSyntax.Square;
					break;
			}
			match = this._preParseSyntaxRegExp.exec(content);
		}
		return syntax;
	}

	backParse(content: string): string {
		let newContent = content;
		newContent = this._backdashArrowParser(newContent);
		newContent = this._listParser(newContent);
		return newContent;
	}

	private _tableParser(content: string) {
		return content.replaceAll(this._table, (table: string) => {
			return table.replaceAll(this._emptyTableCell, (cell: string) => {
				return `${cell}\u00A0`;
			});
		});
	}

	private _listParser(content: string): string {
		return content.replaceAll(this._listWithAnEmptyItem, (listItem: string) => {
			return `${listItem}\u00A0`;
		});
	}

	private _emptyParagraphParser(content: string): string {
		let newContent = content;
		newContent = `\n\n${newContent}`;
		newContent = newContent.replaceAll(
			this._emptyParagraphRegExp,
			(str: string, match: string, offset: number, text: string) => {
				if (!match) return str;
				const indent = sharedIndent(text, offset, offset + str.length);
				const emptyParagraphsCounter = (match.split("\n").length - 2) / 2;
				const res: string[] = [];
				for (let i = 1; i <= emptyParagraphsCounter; i++) res.push(`\n\n${indent}&nbsp;`);
				return str.replace(match, `${res.join("")}\n\n`);
			},
		);
		return newContent.replace(/^\n\n/, "");
	}

	private _escapeDoubleQuotes(content: string): string {
		return content.replaceAll(this._escapeDoubleQuotesRegExp, replaceGroup(`\\"`));
	}

	private _backdashArrowParser(content: string): string {
		return content
			.replaceAll(this._oldArrowRegExp, replaceGroup("->"))
			.replaceAll(this._backArrowRegExp, replaceGroup("->"))
			.replaceAll(this._backDashRegExp, replaceGroup("--"));
	}

	private _includeParse(content: string): string {
		return content.replaceAll(this._includeRegExp, (str: string, group: string, group2: string) => {
			if (!group || !group2) return str;
			const tag = this._tags?.include;
			return tag ? this._parse([null, group2, group], tag) : str;
		});
	}

	private _dashArrowParser(content: string): string {
		return content
			.replaceAll(this._oldArrowRegExp, replaceGroup("→"))
			.replaceAll(this._arrowRegExp, replaceGroup("→"))
			.replaceAll(this._dashRegExp, replaceGroup("—"));
	}

	private _brParser(content: string): string {
		return content.replaceAll(this._brRegExp, (str: string, match: string) =>
			match ? this._parse(["br"], this._tags.br) : str,
		);
	}

	private _kbdParser(content: string): string {
		return content.replaceAll(this._kbdRegExp, (str: string, text: string) =>
			text !== undefined && this._tags.kbd ? this._parse(["kbd", text], this._tags.kbd) : str,
		);
	}

	private _squareBracketsParser(content: string): string {
		return content.replaceAll(this._squareRegExp, (str: string, group: string, hasOpenBracket: string) => {
			let newGroup = group;
			if (!newGroup || hasOpenBracket) return str;
			newGroup = this._screenLink(newGroup);
			const split = newGroup.split(":");
			if (split[0][0] === "/") {
				const tagName = this._replaceNotLowerCase(split[0].slice(1).toLowerCase());
				const tag = this._tags[tagName];
				if (tag?.selfClosing === false) return this._parseClose(tagName);
			}
			const tag = this._tags[this._replaceNotLowerCase(split[0].toLowerCase())];
			return tag ? this._replaceNotLowerCase(this._parse(split, tag)) : str;
		});
	}

	private _idParser(content: string): string {
		return content.replaceAll(this._idRegExp, (str: string, match: string) => {
			if (!match) return str;
			let newMatch = match;
			if (newMatch[1] === ":") newMatch = newMatch.replace(/:/, "");
			return `{% ${newMatch} %}`;
		});
	}

	private _formulaParser(content: string): string {
		return content.replaceAll(this._formulaRegExp, (str: string, firstGroup: string, secondGroup: string) => {
			const group = firstGroup || secondGroup;
			if (!group) return str;
			const tag = this._tags.formula;
			return this._parse([tag.render, group.replaceAll("\\", "\\\\")], tag);
		});
	}

	private _htmlParser(content: string): string {
		let newContent = content;
		newContent = newContent.replaceAll(
			this._findHtmlRegExp,
			// biome-ignore lint/style/useNamingConvention: expected
			(_: string, firstGroup: string, secondGroup: string) => {
				const group = secondGroup;
				if (!group) return _;
				const space = " ".repeat(firstGroup.length);
				// Legacy attributes are positional: [html:<mode>:<scale>]
				const [mode, scale] = (/\[html:?([^\]]*)\]/.exec(_)?.[1] ?? "").split(":");
				const attrs = `mode="${mode || "iframe"}"${scale ? ` scale="${scale}"` : ""}`;
				return `${firstGroup}{%html ${attrs} %}\n${space}\`\`\`\n${secondGroup}\n${space}\`\`\`\n${space}{%/html%}`;
			},
		);

		newContent = newContent.replaceAll(
			this._findHtmlTagRegExp,
			// biome-ignore lint/style/useNamingConvention: expected
			(_: string, firstGroup: string, htmlTag: string, secondGroup: string) => {
				const group = secondGroup;
				if (!group) return _;
				const space = " ".repeat(firstGroup.length);
				return `${firstGroup}${htmlTag}\n${space}\`\`\`\n${secondGroup}\n${space}\`\`\`\n${space}</html>`;
			},
		);

		return newContent;
	}

	private _parse(split: string[], tag: Schema): string {
		let props = [];
		if (tag?.attributes) {
			const attributes = Object.keys(tag.attributes);
			props = split
				.slice(1)
				.map((value, index) => `${attributes[index]}="${this._unScreenLink(this._escapeDoubleQuotes(value))}"`);
		}
		return `{%${tag.render.toLowerCase()} ${props.join(" ")} ${tag.selfClosing === false ? "" : "/"}%}`;
	}

	private _parseClose(tagName: string): string {
		return `{%/${this._replaceNotLowerCase(tagName.toLowerCase())}%}`;
	}

	private _screenLink(content: string): string {
		return content.replaceAll(`://`, `|//`);
	}

	private _unScreenLink(content: string): string {
		return content.replaceAll(`|//`, `://`);
	}

	private _createBlockCodeIgnoreRegExp(reg: string, ...additionalIgnore: string[]): RegExp {
		const commonString = [this._findBlockCodeToIgnore, ...additionalIgnore, reg].join("|");
		return new RegExp(commonString, "gm");
	}

	private _replaceNotLowerCase(content: string): string {
		return content.replaceAll("questionanswer", "questionAnswer");
	}

	private _createIgnoreRegExp(reg: string, ...additionalIgnore: string[]): RegExp {
		const commonString = [
			this._findCommentToIgnore,
			this._findLinkToIgnore,
			this._findInlineCodeToIgnore,
			this._findBlockCodeToIgnore,
			this._findHtmlToIgnore,
			...additionalIgnore,
			reg,
		].join("|");

		return new RegExp(commonString, "gm");
	}

	/**
	 * Replaces every comment with a tag that carries it in hex. Continuation lines lose the indentation
	 * of the line the comment starts on: the formatter puts the container's indentation back. The
	 * comment forms of Markdown links become the same comment, and are written back as `<!-- -->`.
	 */
	private _sourceCommentParser(content: string): string {
		const tag = (text: string) => `{%source-comment hex="${encodeCommentText(text)}" /%}`;

		return content.replaceAll(
			this._sourceCommentRegExp,
			(
				match: string,
				comment: string,
				inParens: string,
				inDoubleQuotes: string,
				inQuotes: string,
				offset: number,
			) => {
				const lineStart = content.lastIndexOf("\n", offset - 1) + 1;
				const before = content.slice(lineStart, offset);

				if (comment) {
					const indent = /^[ \t]*/.exec(before)[0].length;
					const text = comment.slice("<!--".length, -"-->".length);
					return tag(text.replace(new RegExp(`\\n[ \\t]{0,${indent}}`, "g"), "\n"));
				}

				// A link reference owns its line; inside a line the same characters are plain text.
				const title = inParens ?? inDoubleQuotes ?? inQuotes;
				if (title !== undefined && before.trim() === "") return tag(` ${title} `);
				return match;
			},
		);
	}

	private _propertyParser(content: string): string {
		return content.replaceAll(/(\{%property\s[^\n]*?\/%\})/g, (match, tag, offset) => {
			if (offset === 0 || content[offset - 1] === "\n") return match;
			return `\n${tag}`;
		});
	}

	private _preTagParser(content: string): string {
		return content.replaceAll(this._preTagRegExp, (_: string, preContent: string) => {
			let newPreContent = preContent;
			if (!newPreContent.includes("\n")) newPreContent = `\n${newPreContent}\n`;

			return `\`\`\`${newPreContent}\`\`\``;
		});
	}
}
